import { Link } from "wouter";
import { formatDistanceToNow } from "date-fns";
import {
  Archive,
  ArchiveRestore,
  BookOpen,
  FileText,
  Folder as FolderIcon,
  FolderInput,
  MoreHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Document, Folder } from "@/types";
import { useArchiveDocument, useMoveDocument, useRestoreDocument } from "@/hooks/use-library";
import {
  CollectionEmpty,
  CollectionList,
  CollectionLoading,
} from "@/components/layout/CollectionPage";

interface DocumentListProps {
  documents: Document[];
  folders?: Folder[];
  /** Rows are archived, so the row action restores rather than archives. */
  archived?: boolean;
  /**
   * Show which folder each document is filed in. Worth it in mixed lists —
   * "All documents", Recent — and noise when the list is already one folder.
   */
  showFolder?: boolean;
  isLoading?: boolean;
  emptyTitle?: string;
  emptyHint?: string;
  /** Offer "Create your first document" in the empty state. */
  showCreateOnEmpty?: boolean;
}

/**
 * Renders documents — it no longer fetches them.
 *
 * The dashboard, the library and the sidebar all want overlapping slices of the
 * same collection, and when each fetched its own they disagreed after a write.
 * Callers pass rows from `useDocuments()` instead, so there is one request and
 * one cache entry per filter.
 */
export function DocumentList({
  documents,
  folders = [],
  archived = false,
  showFolder = false,
  isLoading = false,
  emptyTitle,
  emptyHint,
  showCreateOnEmpty = false,
}: DocumentListProps) {
  const archiveDocument = useArchiveDocument();
  const restoreDocument = useRestoreDocument();
  const moveDocument = useMoveDocument();
  const folderNames = new Map(folders.map((folder) => [folder.id, folder.name]));

  if (isLoading) {
    return <CollectionLoading label="Loading documents…" />;
  }

  if (documents.length === 0) {
    return (
      <CollectionEmpty
        icon={<FileText className="h-10 w-10 text-muted-foreground" />}
        title={emptyTitle || (archived ? "Nothing archived." : "No documents here yet.")}
        hint={emptyHint}
        action={
          showCreateOnEmpty && !archived ? (
            <Link href="/documents/create">
              <Button>Create your first document</Button>
            </Link>
          ) : undefined
        }
      />
    );
  }

  return (
    <CollectionList>
      {documents.map((doc) => (
        <li key={doc.id} className="group relative">
          <Link href={`/documents/${doc.id}/edit`}>
            <div className="flex cursor-pointer items-center gap-3 px-4 py-3.5 pr-12 transition-colors hover:bg-muted/40">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                {doc.doc_type === "Sermon" ? (
                  <BookOpen className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <FileText className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{doc.title || "Untitled"}</p>
                <p className="truncate text-sm text-muted-foreground">
                  {doc.doc_type || "Document"}
                  {typeof doc.word_count === "number" && doc.word_count > 0
                    ? ` · ${doc.word_count.toLocaleString()} words`
                    : ""}
                  {" · "}
                  {formatDistanceToNow(new Date(doc.updated_at), { addSuffix: true })}
                </p>
              </div>
              {showFolder && doc.folder_id != null && folderNames.has(doc.folder_id) && (
                <span className="hidden shrink-0 items-center gap-1.5 text-sm text-muted-foreground sm:flex">
                  <FolderIcon className="h-3.5 w-3.5" />
                  <span className="max-w-[10rem] truncate">
                    {folderNames.get(doc.folder_id)}
                  </span>
                </span>
              )}
            </div>
          </Link>

          <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={`Actions for ${doc.title || "Untitled"}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link href={`/documents/${doc.id}`}>View</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <FolderInput className="mr-2 h-4 w-4" />
                    Move to folder
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem
                      onClick={() => moveDocument.mutate({ id: doc.id, folderId: null })}
                    >
                      No folder
                    </DropdownMenuItem>
                    {folders.map((folder) => (
                      <DropdownMenuItem
                        key={folder.id}
                        onClick={() =>
                          moveDocument.mutate({ id: doc.id, folderId: folder.id })
                        }
                      >
                        {folder.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                {archived ? (
                  <DropdownMenuItem onClick={() => restoreDocument.mutate(doc.id)}>
                    <ArchiveRestore className="mr-2 h-4 w-4" />
                    Restore
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => archiveDocument.mutate(doc)}>
                    <Archive className="mr-2 h-4 w-4" />
                    Archive
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </li>
      ))}
    </CollectionList>
  );
}
